const DelegateProtocol = require("./ui/delegate").DelegateProtocol;
const BoundsManager = require("./bounds/boundsmanager").BoundsManager;
const Styler = require("./style").Styler;
const CountManager = require("./countmanager").CountManager;
const DataLayer = require("./layers/datalayer");
const _extend = Object.assign;
const StoreEventManager = require("./storeeventmanager");
const geometry = require("./maps/geometry");
const maps = require("./maps");

/**
 * @typedef {Object} BaseViewOptions
 * @property {Style} [style]
 * @property {Number} [zoomLevel]
 */

/**
 * @extends woosmap.utils.MVCObject
 * @memberOf woosmap
 */
class BaseView extends DelegateProtocol {
    /**
     * Constructs a new BaseView
     * ## Delegates
     *
     * - `didLocationMarkerDragEnd` is called when the location marker drag did end.
     *
     * @abstract
     * @since 1.1.0
     */
    constructor() {
        super();
        this._map = null;
        this._currentStore = null;
        this._zoomLevel = null;
        this._zoomEnable = true;
        this._panEnable = true;
        this._interactionLayer = null;
        this._paddedStoreCenter = false;

        this._storeEventManager = new StoreEventManager();
        /**
         * List of stores displayed. This property is bindable.
         * @type {Array.<Store>}
         */
        this.stores = null;
        /**
         * The currently selected store. This property is bindable.
         * @type {Store}
         */
        this.selectedStore = null;

        /**
         * The user defined location. This property is bindable.
         * @type {LatLngLiteral}
         */
        this.location = null;

        /**
         * Controls the viewport padding used by fitBounds.
         * @type {BoundsPadding}
         */
        this.padding = null;

        /**
         * The data styler.
         * @type {woosmap.style.Styler}
         * @private
         */
        this._styler = null;

        this._countMapLoad();
    }

    /**
     * Initializer method to call in subclasses.
     * This is splitted from the constructor to
     * allow subclasses to use it as their prototype.
     * @param {google.maps.Map} map the google maps to manipulate.
     * @param {BaseViewOptions} options map's zoom will be set to this value. If not, no zoom.
     * @protected
     */
    init(map, options) {
        this.options = options || {};
        this._map = map;
        this._styler = new Styler(this.options);

        this._storeEventManager.addInternalListener("click", (store) => {
            this.set("selectedStore", store);
        });

        this._interactionLayer = new DataLayer({
            styler: this._styler,
            eventManager: this._storeEventManager,
            renderer: this.options.interactionRenderer,
        });

        this._interactionLayer.setMap(this._map);

        this._zoomLevel = this.options.zoomLevel;
        this._setupInteraction(this.options.style);
    }

    /**
     * Adds the listeners on the map and prepare the markers properties
     * @private
     */
    _setupInteraction() {
        /**
         * This marker is positioned on the map when `location` property changes.
         * If this marker is set draggable (`marker.setDraggable(true)`) the location
         * property will be changed when dropping the marker (ie. when the marker fires the `dragend` event).
         * @type {google.maps.Marker}
         */
        this.marker = maps.utils.getMarker();
        maps.events.addListener(this.marker, "dragend", (event) => {
            this.set("location", { lat: event.latLng.lat(), lng: event.latLng.lng() });
            this.callDelegateMethod("didLocationMarkerDragEnd");
        });
    }

    /**
     * Centers the map on the store, and zoom on it if the current zoom
     * is lesser than defined zoomLevel.
     * @private
     */
    _centerOnStoreIfNeeded(store) {
        const currentZoom = this._map.getZoom();

        if (this._zoomLevel && this._zoomEnable) {
            if (currentZoom < this._zoomLevel) {
                this._map.setZoom(this._zoomLevel);
            }
        }
        if (this._panEnable) {
            const coordinates = store.geometry.coordinates;
            const center = maps.utils.getLatLng(coordinates[1], coordinates[0]);
            this.panTo(center);
        }
    }

    /**
     * Builds a boundManager, if no padding is defined, returns null.
     * @param {BoundsPadding} [padding]
     * @returns {BoundsManager|null}
     * @private
     */
    _getBoundsManager(padding) {
        const map = maps.utils.getMapDiv(this._map);
        let paddingOptions = this.padding || {};

        if (padding) {
            paddingOptions = _extend(paddingOptions, padding);
        }

        if (Object.keys(paddingOptions).length !== 0) {
            return new BoundsManager({
                mapWidth: map.offsetWidth,
                mapHeight: map.offsetHeight,
                padding: paddingOptions,
            });
        }
        return null;
    }

    /**
     * Sets the center of the map.
     * Takes in account the `padding` values to displace the viewport.
     * @param {google.maps.LatLng} center
     * @param {BoundsPadding} [padding]
     * @param {Boolean} [force] forces the pan even if the new center is within visible bounds.
     */
    panTo(center, padding, force) {
        if (this._paddedStoreCenter || padding) {
            const boundsManager = this._getBoundsManager(padding);
            if (boundsManager) {
                const updateFunction = () => {
                    const currentBounds = this._map.getBounds();
                    const currentZoom = this._map.getZoom();
                    const paddedCenter = boundsManager.getPaddedCenter(center, this._zoomLevel || currentZoom);
                    const visibleBounds = boundsManager.getVisibleBounds(currentBounds, currentZoom);

                    if (!visibleBounds.contains(center) || force) {
                        this._map.panTo(maps.utils.getLatLng(paddedCenter.lat, paddedCenter.lng));
                    }
                };

                if (this._map.getBounds() === undefined) {
                    maps.events.addListenerOnce(this._map, "idle", updateFunction);
                } else {
                    updateFunction();
                }
            } else {
                this._map.panTo(center);
            }
        } else {
            this._map.panTo(center);
        }
    }

    /**
     * Sets the viewport to contain the given bounds.
     * Takes in account the `padding` values to displace the viewport.
     * @param {google.maps.LatLngBounds} bounds
     * @param {BoundsPadding} [padding] padding to apply to the viewport.
     */
    fitBounds(bounds, padding) {
        const boundsManager = this._getBoundsManager(padding);
        if (bounds) {
            if (boundsManager) {
                const zoomAndCenter = boundsManager.getBoundsCenterAndZoom(bounds);
                const center = zoomAndCenter.center;

                maps.utils.centerAndZoom(this._map, maps.utils.getLatLng(center.lat, center.lng), zoomAndCenter.zoom);
            } else {
                maps.utils.fitBounds(this._map, bounds);
            }
        }
    }

    /**
     * Enable or Disable zoom when select a store
     * @param {boolean} activate (e.g. true or false)
     */
    enableZoom(activate) {
        this._zoomEnable = activate;
    }

    /**
     * Enable or Disable pan when select a store
     * @param {boolean} activate (e.g. true or false)
     */
    enablePan(activate) {
        this._panEnable = activate;
    }

    /**
     * Enable or Disable padded Store Center the padded store center triggers panning only if the selected store
     * is outside the 'visible zone' ie. the current map bounds minus padding.
     * @param {Boolean} activate
     */
    enablePaddedStoreCenter(activate) {
        this._paddedStoreCenter = activate;
    }

    /**
     * This is the function called when the stores property has changed.
     */
    stores_changed() {
        const stores = this.get("stores");
        if (stores) {
            this._interactionLayer.clear();
            const storesFeatureCollection = { type: "FeatureCollection", features: stores };
            if (stores[0] && stores[0].properties.distance !== undefined) {
                this._interactionLayer.displayNumberedStores(storesFeatureCollection);
            } else {
                this._interactionLayer.displayStores(storesFeatureCollection);
            }

            const location = this.get("location");
            if (stores.length > 0) {
                const bounds = this.getDataBounds();

                if (location) {
                    bounds.extend(new geometry.LatLng(location.lat, location.lng));
                    this.fitBounds(bounds);
                } else if (stores.length > 1) {
                    this.fitBounds(bounds);
                } else if (stores.length === 1) {
                    this._centerOnStoreIfNeeded(stores[0]);
                }
            }
        }
    }

    /**
     * This function is called when `selectedStore` property is changed
     */
    selectedStore_changed() {
        const selectedStore = this.get("selectedStore");
        if (selectedStore) {
            this._centerOnStoreIfNeeded(selectedStore);
        }

        this._interactionLayer.selectStore(selectedStore);
    }

    /**
     * Process each feature in the dataLayer to compute the bounds of the displayed data.
     * @returns {google.maps.LatLngBounds|null}
     */
    getDataBounds() {
        const stores = this.get("stores");
        if (stores && stores.length > 0) {
            const bounds = new geometry.LatLngBounds();

            stores.forEach((marker) => {
                bounds.extend(new geometry.LatLng(marker.geometry.coordinates[1], marker.geometry.coordinates[0]));
            });

            return bounds;
        }

        return null;
    }

    /**
     * Returns the google maps used by the BaseView.
     * @returns {google.maps.Map}
     */
    getMap() {
        return this._map;
    }

    /**
     * Removes all the stores from the map.
     */
    removeAllStores() {
        this._interactionLayer.clear();
        this.set("stores", null);
    }

    /**
     * Adds a single store to the map, zoom + pan on it
     * @param {Store} store the store to display
     */
    displayStore(store) {
        this._map.setZoom(16);
        this.panTo(new geometry.LatLng(store.geometry.coordinates[1], store.geometry.coordinates[0]));
        // ensure the store is in the interactionLayer.
        this._interactionLayer.displayStores([store]);
    }

    /**
     * This function is called when `location` property is changed
     */
    location_changed() {
        const newLocation = this.get("location");
        if (newLocation !== null) {
            maps.utils.setPosition(this.marker, maps.utils.getLatLng(newLocation.lat, newLocation.lng));
            maps.utils.setMap(this.marker, this._map);
        } else {
            maps.utils.setMap(this.marker, null);
        }
    }

    /**
     * Count a map loading.
     * @private
     */
    _countMapLoad() {
        const countManager = new CountManager();
        countManager.count({ kind: "map_load" });
    }

    /**
     * Adds a store event listener.
     * @param {EventName} eventName
     * @param {StoreEventCallback} callback
     * @returns {StoreEventListener}
     */
    addListener(eventName, callback) {
        return this._storeEventManager.addListener(eventName, callback);
    }

    /**
     * Removes a store event listener.
     * @param {StoreEventListener} storeEventListener
     */
    removeListener(storeEventListener) {
        this._storeEventManager.removeEventListener(storeEventListener);
    }
}

exports.BaseView = BaseView;
